import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import faixaCampanha from '../assets/acampateens-faixa.jpg';
import { AvisoTaxaCartao } from '../componentes/AvisoTaxaCartao.tsx';
import { Marca } from '../componentes/Marca.tsx';
import { BotaoTema } from '../componentes/Tema.tsx';
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Etiqueta,
  Icone,
  TextoFormatado,
} from '../componentes/ui/index.ts';
import { api, data, ErroApi, moeda } from '../lib/api.ts';
import { listar, salvar, type RascunhoLocal } from '../lib/rascunhos.ts';
import type { ConfigPublica, RespostaIniciar } from '../lib/tipos.ts';

export function Inicio() {
  const navegar = useNavigate();
  const [config, setConfig] = useState<ConfigPublica | null>(null);
  const [rascunhos, setRascunhos] = useState<RascunhoLocal[]>([]);

  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    setRascunhos(listar());
    api<ConfigPublica>('/api/inscricoes/config')
      .then(setConfig)
      .catch(() =>
        setConfig({
          formOpen: true,
          closedMessage: '',
          valorPadrao: '0',
          dataAcampamento: null,
          local: '',
          periodo: '',
          instrucoesPagamento: '',
        }),
      );
  }, []);

  async function iniciar(confirmarNomeParecido = false) {
    setErro(null);
    setEnviando(true);

    try {
      const r = await api<RespostaIniciar>('/api/inscricoes/iniciar', {
        metodo: 'POST',
        corpo: { email, nomeAdolescente: nome, confirmarNomeParecido },
      });

      salvar({
        token: r.inscricao.token,
        email: r.inscricao.email,
        nomeAdolescente: r.inscricao.nomeAdolescente,
        step: r.inscricao.step,
        updatedAt: r.inscricao.atualizadoEm,
      });

      navegar(`/inscricao/${r.inscricao.token}`, {
        state: { aviso: r.aviso, saudeOmitida: r.saudeOmitida },
      });
    } catch (e) {
      setErro(e instanceof ErroApi ? e : new ErroApi(0, 'desconhecido', 'Erro inesperado'));
    } finally {
      setEnviando(false);
    }
  }

  const fechado = config && !config.formOpen;
  const valor = config && Number(config.valorPadrao) > 0 ? config.valorPadrao : null;
  // `periodo` é o texto livre digitado em Configurações; `dataAcampamento` é
  // um campo mais antigo, sem tela de edição própria hoje — mantido só como
  // fallback para configs que nunca chegaram a preencher o texto livre.
  const periodoExibido = config?.periodo || (config?.dataAcampamento ? data(config.dataAcampamento) : '');

  return (
    <div className="container container-form" style={{ paddingBlock: 'var(--gap-6)' }}>
      <header className="linha linha-entre" style={{ marginBottom: 'var(--gap-8)' }}>
        <Marca />
        <BotaoTema />
      </header>

      {/* Faixa tática: a linha de dados que ancora o cartaz na realidade —
          quem publica, qual edição. Sangra até a borda da tela. */}
      <div
        className="linha linha-entre coordenada sangria"
        style={{
          borderBlock: 'var(--fio) solid var(--borda)',
          padding: 'var(--gap-2) var(--gap-5)',
          marginBottom: 'var(--gap-6)',
        }}
      >
        <span>Igreja Bíblica da Paz · Ministério de adolescentes</span>
        <span>Edição 2027</span>
      </div>

      <main id="conteudo" className="pilha-6">
        {/* O letreiro é o h1 de verdade, composto em texto sobre a faixa da
            arte da campanha. A imagem entra decorativa (alt vazio) porque
            quem carrega o significado é o texto por cima dela. */}
        <section className="cartaz sangria">
          <img className="cartaz__foto" src={faixaCampanha} alt="" />
          <Etiqueta>{fechado ? 'Inscrições encerradas' : 'Inscrições abertas'}</Etiqueta>
          <h1 className="letreiro">
            <span className="letreiro__pixel">A</span>campa
            <br />
            teens<span className="letreiro__ano">&rsquo;27</span>
          </h1>
        </section>

        {/* Serifada em itálico: o respiro entre dois blocos de grito. */}
        <p
          className="editorial texto-suave"
          style={{ fontSize: 'clamp(1.25rem, 3.5vw, 1.75rem)', maxWidth: '28ch' }}
        >
          A ficha é preenchida pelo responsável. Dá para parar no meio e continuar depois —
          salvamos automaticamente.
        </p>

        {/* Tudo que o admin preencheu em Configurações aparece aqui, sempre —
            não só quando o formulário está fechado. É a capa: quem chega
            precisa ver valor, data, local e condições de pagamento antes de
            decidir se começa a ficha. */}
        {(valor || periodoExibido || config?.local) && (
          <div
            className="sangria"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              borderBlock: 'var(--fio) solid var(--borda-forte)',
            }}
          >
            {valor && (
              <div
                style={{
                  flex: '1 1 13rem',
                  padding: 'var(--gap-4) var(--gap-5)',
                  containerType: 'inline-size',
                }}
              >
                <span className="dymo">Valor do acampamento</span>
                <div className="numeral destaque" style={{ marginBlock: 'var(--gap-3) var(--gap-2)' }}>
                  {moeda(valor)}
                </div>
                <p className="coordenada">A inscrição em si é gratuita</p>
              </div>
            )}
            {periodoExibido && (
              <div
                style={{
                  flex: '1 1 15rem',
                  padding: 'var(--gap-4) var(--gap-5)',
                  borderLeft: 'var(--fio) solid var(--borda)',
                }}
              >
                <span className="dymo">Data e horário</span>
                {/* Texto corrido: "25 a 28 de março..." não cabe como número
                    de destaque, é para ler. */}
                <p style={{ marginTop: 'var(--gap-3)', fontSize: 'var(--texto-lg)' }}>
                  {periodoExibido}
                </p>
              </div>
            )}
            {config?.local && (
              <div
                style={{
                  flex: '1 1 16rem',
                  padding: 'var(--gap-4) var(--gap-5)',
                  borderLeft: 'var(--fio) solid var(--borda)',
                }}
              >
                <span className="dymo">Local</span>
                {/* Texto corrido, não `numeral`: endereço é para ler, não
                    para bater o olho como número. */}
                <p style={{ marginTop: 'var(--gap-3)', fontSize: 'var(--texto-lg)' }}>
                  {config.local}
                </p>
              </div>
            )}
          </div>
        )}

        {(config?.instrucoesPagamento || valor) && (
          <Cartao titulo="Condições de pagamento">
            <div className="pilha-2">
              {config?.instrucoesPagamento && (
                <TextoFormatado texto={config.instrucoesPagamento} />
              )}
              {valor && <AvisoTaxaCartao valor={valor} />}
            </div>
          </Cartao>
        )}

        {fechado && (
          <Aviso tipo="alerta">
            <TextoFormatado texto={config.closedMessage} />
          </Aviso>
        )}

        {/* Retomada pelo aparelho: um clique, sem redigitar nada. */}
        {rascunhos.length > 0 && (
          <Cartao marcado titulo="Continuar de onde parou">
            <div className="pilha-2">
              {rascunhos.map((r) => (
                <Botao
                  key={r.token}
                  variante="secundario"
                  largo
                  onClick={() => navegar(`/inscricao/${r.token}`)}
                  iconeDireita={<Icone.Seta style={{ rotate: '-90deg' }} />}
                >
                  <span className="crescer" style={{ textAlign: 'left' }}>
                    {r.nomeAdolescente}
                  </span>
                  <span className="texto-fraco">passo {r.step}</span>
                </Botao>
              ))}
            </div>
          </Cartao>
        )}

        {!fechado && (
          <Cartao titulo={rascunhos.length > 0 ? 'Nova inscrição' : 'Começar'}>
            <form
              className="pilha"
              onSubmit={(e) => {
                e.preventDefault();
                void iniciar();
              }}
            >
              <p className="campo__dica">
                Estes dois dados identificam a inscrição. Se você já começou em outro aparelho,
                preencha os mesmos e continuamos de onde parou.
              </p>

              <Campo
                rotulo="Seu e-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="voce@exemplo.com"
                required
                obrigatorio
                dica="É onde enviamos a confirmação e os avisos."
              />

              <Campo
                rotulo="Nome completo do adolescente"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                obrigatorio
                minLength={3}
              />

              {erro?.codigo === 'nome_parecido' ? (
                <Aviso tipo="alerta">
                  {erro.mensagem}
                  <div style={{ marginTop: 'var(--gap-3)' }}>
                    <Botao tamanho="pequeno" onClick={() => void iniciar(true)} disabled={enviando}>
                      É outra pessoa, continuar
                    </Botao>
                  </div>
                </Aviso>
              ) : (
                erro && <Aviso tipo="erro">{erro.mensagem}</Aviso>
              )}

              <Botao
                type="submit"
                tamanho="grande"
                largo
                carregando={enviando}
                disabled={!email || !nome}
              >
                Começar inscrição
              </Botao>
            </form>
          </Cartao>
        )}
      </main>

      {/* Rodapé de prova de impressão: barra de código e carimbo. É enfeite
          gráfico assumido — daí o aria-hidden na barra, que não carrega
          informação nenhuma. */}
      <footer
        className="linha linha-entre"
        style={{ marginTop: 'var(--gap-8)', paddingTop: 'var(--gap-5)', borderTop: 'var(--fio) solid var(--borda)' }}
      >
        <div className="pilha-2">
          <div className="codigo-barras" style={{ width: '9rem' }} aria-hidden="true" />
          <span className="coordenada">Acampamento TeensIBP · Edição 2027</span>
        </div>
        <span className="carimbo texto-fraco">
          Teens
          <br />
          IBP
          <br />
          2027
        </span>
      </footer>
    </div>
  );
}
